// Toast + ToastProvider — floating notifications per COMPONENTS §Catalog.
//
// Wired to `toastStore` (Zustand — first store, creates frontend/src/stores/).
// Per COMPONENTS §Per-primitive WCAG:
//   - Single role="status" aria-live="polite" container provided here.
//   - Non-critical (success / info / warning) auto-dismiss at 5s.
//   - Critical (error) does NOT auto-dismiss — user must close explicitly.
//   - 4 variants with Phosphor icons (CheckCircle / Warning / XCircle / Info).
//   - Max 3 visible at once (oldest dropped silently in the store).

import { useEffect, useState, type ReactNode } from 'react';
import { icons } from './icons.js';
import { useTranslation } from 'react-i18next';
import { useToastStore, type ToastItem, type ToastVariant, type ToastAction } from '../../stores/toastStore.js';
import { Button } from './Button.js';
import { IconButton } from './IconButton.js';

const AUTO_DISMISS_MS = 5_000;

// The surface is the OPAQUE overlay token (--color-bg-overlay); the variant colour
// is a subtle WASH layered *over* that opaque base (not a translucent bg that
// replaces it — that let page content read through). Border + icon carry the
// variant too. `border` + `wash` are separate so the base stays opaque.
const variantStyle: Record<ToastVariant, { border: string; wash: string; icon: ReactNode }> = {
  success: {
    border: 'border-[var(--color-success)]/40',
    wash: 'bg-[var(--color-success)]/10',
    icon: <icons.statusSuccess aria-hidden weight="fill" size={20} className="text-[var(--color-success)] shrink-0" />,
  },
  warning: {
    border: 'border-[var(--color-warning)]/40',
    wash: 'bg-[var(--color-warning)]/10',
    icon: <icons.statusWarning aria-hidden weight="fill" size={20} className="text-[var(--color-warning)] shrink-0" />,
  },
  error: {
    border: 'border-[var(--color-danger)]/40',
    wash: 'bg-[var(--color-danger)]/10',
    icon: <icons.statusError aria-hidden weight="fill" size={20} className="text-[var(--color-danger)] shrink-0" />,
  },
  info: {
    border: 'border-[var(--color-info)]/40',
    wash: 'bg-[var(--color-info)]/10',
    icon: <icons.statusInfo aria-hidden weight="fill" size={20} className="text-[var(--color-info)] shrink-0" />,
  },
};

function ToastEntry({ item }: { item: ToastItem }) {
  const remove = useToastStore((s) => s.remove);
  const { t } = useTranslation('common');
  const v = variantStyle[item.variant];

  // Auto-dismiss pauses while hovered or focus is inside (WCAG 2.2.1 Timing
  // Adjustable) — the timer restarts in full on resume.
  const [paused, setPaused] = useState(false);
  // `leaving` swaps the enter slip (o-toast-in) for the exit slip (o-toast-out).
  // The two use DISTINCT keyframes, so swapping them on this persistent element
  // restarts the animation (a same-name swap would not — that was the original
  // "won't close" bug). The store row is dropped when the exit slip ends, via
  // onAnimationEnd — which self-syncs with the CSS duration, including the ~0ms
  // collapse under prefers-reduced-motion, so no JS timing constant mirrors the token.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (item.variant === 'error') return; // errors do not auto-dismiss
    if (paused || leaving) return;
    const handle = setTimeout(() => setLeaving(true), AUTO_DISMISS_MS);
    return () => clearTimeout(handle);
  }, [item.variant, paused, leaving]);

  return (
    <div
      className={`relative overflow-hidden border rounded-md shadow-overlay bg-[var(--color-bg-overlay)] pointer-events-auto ${v.border} ${leaving ? 'o-toast-out' : 'o-toast-in'}`}
      // When the exit slip ends, drop the row. Guard currentTarget because
      // animationend bubbles — ignore any (future) animated child's event.
      onAnimationEnd={(e) => {
        if (leaving && e.target === e.currentTarget) remove(item.id);
      }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      {/* Variant wash — a subtle tint OVER the opaque surface, not a see-through bg. */}
      <span aria-hidden className={`pointer-events-none absolute inset-0 ${v.wash}`} />
      {/* Content sits above the wash (relative → paints over the absolute layer). */}
      <div className="relative flex items-center gap-3 p-3 pe-2 text-[var(--color-text-primary)]">
        {v.icon}
        <div className="min-w-0 flex-1 wrap-anywhere text-sm">{item.message}</div>
        {item.action && (
          <Button
            variant="ghost"
            className="-my-2 shrink-0"
            onClick={() => {
              item.action?.onClick();
              setLeaving(true);
            }}
          >
            {item.action.label}
          </Button>
        )}
        <IconButton
          aria-label={t('actions.dismiss')}
          variant="ghost"
          onClick={() => setLeaving(true)}
          // -my-2 lets the 40px min-height tap target overlap the padding so it does
          // not inflate a single-line toast; items-center keeps everything centered.
          className="-my-2 -me-1"
        >
          <icons.close aria-hidden size={16} />
        </IconButton>
      </div>
    </div>
  );
}

export interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const toasts = useToastStore((s) => s.toasts);
  return (
    <>
      {children}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-4 end-4 z-toast flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      >
        {toasts.map((item) => (
          <ToastEntry key={item.id} item={item} />
        ))}
      </div>
    </>
  );
}

/** What a toast may carry beside its words: one act ("Undo" · "Open"), and a
 *  key — a keyed toast replaces its predecessor instead of stacking. */
export interface ToastOptions {
  action?: ToastAction;
  key?: string;
}

/** Convenience hook for emitting toasts. Use anywhere; no Provider needed. */
export function useToast() {
  const add = useToastStore((s) => s.add);
  return {
    success: (message: string, opts?: ToastOptions) => add({ message, variant: 'success', action: opts?.action, key: opts?.key }),
    error: (message: string, opts?: ToastOptions) => add({ message, variant: 'error', action: opts?.action, key: opts?.key }),
    warning: (message: string, opts?: ToastOptions) => add({ message, variant: 'warning', action: opts?.action, key: opts?.key }),
    info: (message: string, opts?: ToastOptions) => add({ message, variant: 'info', action: opts?.action, key: opts?.key }),
  };
}
